import type { NuxtError } from 'nuxt/app'
import fs from 'node:fs'
import path from 'node:path'
import process from 'node:process'
import { createError } from '#imports'
import { consola } from 'consola'
import yaml from 'yaml'

export const BYPASS_COOKIE_NAME = 'maintenance-mode-bypass'

export interface MaintenanceModeConfig {
  enabled: boolean
  bypassSecret: string | null
  include: string[] | null
  exclude: string[] | null
}

/**
 * 将给定的 URL 与一个模式数组进行匹配。
 *
 * @param url 要匹配的 URL。
 * @param patterns 要匹配的 URL 模式数组。
 * @returns {boolean} URL 是否与任意一个模式匹配。
 */
export function urlMatches(url: string, patterns: string[]): boolean {
  for (const pattern of patterns) {
    // 从模式中创建正则表达式。
    //
    //  -`^`：匹配字符串的开头。
    //  -`$`：匹配字符串的末端。
    //  -`。*`：匹配任何零或更多次字符。
    //  -`\/`：匹配字面的前向斜线。
    const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`)

    if (regex.test(url)) {
      return true
    }
  }

  return false
}

/**
 * 快速确定是否启用了维护模式。
 * 使用它来确定是否应该开始评估维护模式。
 *
 * @returns {Promise<boolean>} 是否启用了维护模式。
 */
export async function isMaintenanceEnabled(): Promise<boolean> {
  const config = await getMaintenanceModeConfig()
  return config.enabled
}

/**
 * 确定站点是否正在维护。
 *
 * @param path 请求的路径。
 * @param bypassCookieValue 旁路 Cookie 的值。
 * @param maintenanceConfig 维护模式配置。
 *
 * @returns {boolean} 站点是否正在维护。
 */
export function isUnderMaintenance(
  path: string,
  bypassCookieValue: string | null,
  maintenanceConfig: MaintenanceModeConfig,
): boolean {
  const { enabled, bypassSecret, include, exclude } = maintenanceConfig

  if (!enabled) {
    return false
  }

  if (bypassSecret && bypassCookieValue && bypassCookieValue === bypassSecret) {
    return false
  }

  if (include && !urlMatches(path, include)) {
    return false
  }

  if (exclude && urlMatches(path, exclude)) {
    return false
  }

  return true
}

/**
 * 获取维护模式配置。
 *
 * @returns {Promise<MaintenanceModeConfig>} 维护模式配置。
 */
export async function getMaintenanceModeConfig(): Promise<MaintenanceModeConfig> {
  const config: MaintenanceModeConfig = {
    enabled: false,
    bypassSecret: null,
    include: null,
    exclude: null,
  }

  if (import.meta.server) {
    const filePath = path.join(process.cwd(), 'public', 'maintenance.yaml')
    // 检查文件是否存在
    if (!fs.existsSync(filePath)) {
      config.enabled = false
      config.bypassSecret = ''
      config.include = null
      config.exclude = null
    }
    else {
      const fileContent = fs.readFileSync(filePath, 'utf8')
      const data = yaml.parse(fileContent)
      if (data) {
        config.enabled = data.enabled
        config.bypassSecret = data.bypassSecret
        config.include = data.include
        config.exclude = data.exclude
      }
    }
  }

  if (import.meta.client) {
    const data = await $fetch<MaintenanceModeConfig>('/api/maintenance', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 3000,
    })
    if (data) {
      config.enabled = data.enabled
      config.bypassSecret = data.bypassSecret
      config.include = data.include
      config.exclude = data.exclude
    }
  }

  const enabled = config.enabled ?? false
  const bypassSecret = config.bypassSecret ?? null
  const include = config.include ?? null
  const exclude = config.exclude ?? null

  return {
    enabled,
    bypassSecret,
    include,
    exclude,
  }
}

/**
 * 抛出维护错误。
 *
 * @throws {NuxtError | void} 503 错误，消息为 站点正在维护中。
 */
export function throwMaintenanceError(): NuxtError | void {
  throw createError({
    statusCode: 503,
    statusMessage: '站点正在维护中',
  })
  // 显示维护页面
}

export async function genMaintenance() {
  const route = useRoute()
  // 如果未启用维护模式
  const isMaintenanced = await isMaintenanceEnabled()
  if (!isMaintenanced) {
    return false
  }

  const maintenanceConfig = await getMaintenanceModeConfig()

  if (import.meta.server) {
    // 在服务端获取cookie

  }
  // 处理 GETTING 绕过
  const bypassCookie = useCookie(BYPASS_COOKIE_NAME, {
    maxAge: 60 * 60 * 24 * 7 * 4, // 1 month
    sameSite: 'strict',
    secure: true,
  })
  let bypassCookieValue: string | null = bypassCookie.value ?? null

  // 处理设置绕过
  if (
    maintenanceConfig.bypassSecret
    && route.query.bypass === maintenanceConfig.bypassSecret
  ) {
  // 如果存在绕过查询参数，并且它与绕过密钥匹配，则设置绕过 cookie。
  // 这用于在后续请求中绕过维护模式。
    bypassCookie.value = maintenanceConfig.bypassSecret
    bypassCookieValue = maintenanceConfig.bypassSecret
  }

  // 处理维护检查
  if (!isUnderMaintenance(route.path, bypassCookieValue, maintenanceConfig)) {
    return false
  }

  return true
}
